import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  IconButton,
  Typography,
  MenuItem,
  Alert,
} from '@mui/material';
import { TenantDateField } from '../../../components/TenantDateField';
import { DeleteOutline, AddCircleOutline } from '@mui/icons-material';
import { DocumentData } from 'firebase/firestore';
import { InstallmentDto } from '../../../lib/api/tuition.api';

interface EditTuitionInstallmentPlanDialogProps {
  open: boolean;
  plan: DocumentData | null;
  onClose: () => void;
  onConfirm: (installments: InstallmentDto[], isAdminOverride: boolean) => Promise<void>;
}

export function EditTuitionInstallmentPlanDialog({
  open,
  plan,
  onClose,
  onConfirm,
}: EditTuitionInstallmentPlanDialogProps) {
  const [installments, setInstallments] = useState<InstallmentDto[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && plan) {
      // deeply clone to avoid mutability issues
      setInstallments(JSON.parse(JSON.stringify(plan.installments || [])));
      setSubmitting(false);
      setError(null);
    }
  }, [open, plan]);

  const handleChange = (index: number, field: keyof InstallmentDto, value: any) => {
    const updated = [...installments];
    updated[index] = { ...updated[index], [field]: value } as InstallmentDto;
    setInstallments(updated);
  };

  const handleRemove = (index: number) => {
    const updated = [...installments];
    updated.splice(index, 1);
    setInstallments(updated);
  };

  const handleAdd = () => {
    const newCount = installments.length + 1;
    setInstallments([
      ...installments,
      {
        id: `inst-new-${Date.now()}`,
        label: `Cuota ${newCount}`,
        dueDate: new Date().toISOString().substring(0, 10),
        amount: 0,
        type: 'monthly',
      } as InstallmentDto,
    ]);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Pass true for isAdminOverride since this dialog is explicitly for admin edits
      await onConfirm(installments, true);
      onClose();
    } catch (e: any) {
      console.error(e);
      setError(e?.message ?? 'Error inesperado al guardar las cuotas');
    } finally {
      setSubmitting(false);
    }
  };

  if (!plan) return null;

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Editar Cuotas del Plan (Admin)</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Modifica los montos, fechas y cantidad de cuotas. Esta operación sobreescribe las
            validaciones automáticas (isAdminOverride habilitado), permitiendo forzar el estado.
          </Typography>

          {installments.map((inst, index) => (
            <Stack
              key={index}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems="center"
            >
              <TextField
                label="Identificador"
                size="small"
                value={inst.id}
                onChange={(e) => handleChange(index, 'id', e.target.value)}
                sx={{ width: { sm: 120 } }}
              />
              <TextField
                label="Etiqueta"
                size="small"
                value={inst.label}
                onChange={(e) => handleChange(index, 'label', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TenantDateField
                label="Fecha Vencimiento"
                size="small"
                value={inst.dueDate}
                onChange={(v) => handleChange(index, 'dueDate', v)}
              />
              <TextField
                label="Monto"
                type="number"
                size="small"
                value={inst.amount}
                onChange={(e) => handleChange(index, 'amount', Number(e.target.value))}
                sx={{ width: { sm: 140 } }}
              />
              <TextField
                select
                label="Tipo"
                size="small"
                value={inst.type}
                onChange={(e) =>
                  handleChange(index, 'type', e.target.value as 'enrollment' | 'monthly')
                }
                sx={{ width: { sm: 130 } }}
              >
                <MenuItem value="enrollment">Matrícula</MenuItem>
                <MenuItem value="monthly">Mensual</MenuItem>
              </TextField>
              <IconButton color="error" onClick={() => handleRemove(index)} disabled={submitting}>
                <DeleteOutline />
              </IconButton>
            </Stack>
          ))}

          <Button
            startIcon={<AddCircleOutline />}
            onClick={handleAdd}
            disabled={submitting}
            sx={{ alignSelf: 'flex-start' }}
          >
            Añadir Cuota
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
